'use client';

import React from 'react';
import { DynamicForm } from '@/components/forms/dynamic-form/DynamicForm';
import { useFormSchema } from '@/hooks/useFormSchema';
import { FieldValues, UseFormSetError } from 'react-hook-form';
import { ArrowButton, ArrowButtonType } from '@/components/arrow-button/ArrowButton';
import styles from './Recoverpassword.module.scss';
import { useTranslation } from '@app/i18n/useTranslationClient';
import Image from 'next/image';
import { createSchema, IAPIFormSchema, ISchema } from '@/services/formSchema.service';
import { useRecoverPasswordFormSchema } from '@/hooks/api/useRecoverPasswordFormSchema/useRecoverPasswordFormSchema';
import { useRecoveryPassword } from '@/hooks/api/useRecoveryPassword/useRecoveryPassword';
import { handleFormServerErrors } from '@/components/dynamic-field/field-input/fieldInput.helper';

interface IRecoverPassword {
    onPrevStep?(): void;
}

export const RecoverPassword = ({ onPrevStep }: IRecoverPassword) => {
    const [isFormSubmitted, setSubmitForm] = React.useState(false);
    const [schema, setSchema] = React.useState<ISchema[] | null>(null);

    const recoverPasswordFormSchema = useRecoverPasswordFormSchema();
    const { recover, loading } = useRecoveryPassword();
    const {
        t,
        i18n: { language },
    } = useTranslation();

    React.useEffect(() => {
        if (!recoverPasswordFormSchema.loading) {
            recoverPasswordFormSchema.getSchema().then((schema) => {
                setSchema(createSchema(schema as unknown as IAPIFormSchema, language, t));
            });
        }
    }, []);

    if (!schema) {
        return null;
    }

    const onFormSubmit = async (data: FieldValues, setErrors: UseFormSetError<any>) => {
        const response = await recover({ _username: data._username });

        if (response.errors) {
            handleFormServerErrors(setErrors, response.errors);
        } else {
            setSubmitForm(true);
        }
    };

    if (isFormSubmitted) {
        return (
            <div className={styles.recover}>
                <div className={styles.recoverTitle}>{t('translations.recover.form.resetPassword')}</div>
                <div className={styles.recoverSuccessWrapper}>
                    <Image
                        data-testid={'loginBtnIcon'}
                        src={'/icons/contactBlack.svg'}
                        alt={'login button'}
                        className={styles.recoverImage}
                        width={40}
                        height={40}
                    />
                    <div className={styles.recoverLabel}>{t('translations.recover.form.successLabel')}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.recover} data-testid="recoverPassword">
            <div className={styles.recoverTitle}>{t('translations.recover.form.resetPassword')}</div>
            <div className={styles.recoverLabel}>{t('translations.recover.form.label')}</div>
            <div className={styles.recoverForm}>
                <DynamicForm
                    formSchema={schema}
                    formSubmitButtonLabel={t('translations.forms.send')}
                    onFormSubmit={onFormSubmit}
                    omitSubmitButtonMargin={true}
                    isSubmitButtonDisabled={loading}
                />
            </div>
            <div>
                <ArrowButton onClick={onPrevStep} classes={styles.recoverBack} type={ArrowButtonType.BLACK}>
                    {t('translations.recover.form.backToLogin')}
                </ArrowButton>
            </div>
        </div>
    );
};
